/**
 *  @class
 *  @function SearchFallback
 *
 *  Second-chance search. When Shopify's search returns nothing for a term, this
 *  element asks an external service for products that do match:
 *
 *      GET <endpoint>/search?query=<term>
 *      200 -> { query, count, handles: [...], results: [{ handle, title }] }
 *      404 -> the service has no match either (nothing is rendered)
 *
 *  The returned handles are turned into real theme markup by requesting the
 *  `search-fallback-results` section (see that file), so product rows and cards
 *  stay identical to the ones Liquid renders — including prices and money
 *  formatting.
 *
 *  Two modes:
 *    drawer — listens for `search:no-results` from <predictive-search-v2> and
 *             replaces the drawer's empty state with fallback product rows.
 *    grid   — runs on load on a /search page that returned no results and
 *             replaces the empty state with a fallback product grid.
 *
 *  Request budget (drawer mode gets an event per keystroke that finds nothing):
 *    - terms shorter than `data-min-length` never leave the browser
 *    - `data-debounce` ms of quiet time before a term is looked up at all
 *    - every answer is cached per term, misses included, so re-typing,
 *      backspacing or re-opening the drawer costs no request
 *
 *  While a term is on its way to the service a skeleton placeholder is shown, so
 *  the shopper is not left looking at a final-sounding "no results" during the
 *  round trip. It is inserted next to the theme's own empty state rather than
 *  replacing it: if the fallback comes back with nothing, removing the skeleton
 *  is all it takes to be back where we started.
 *
 *  Every failure path is silent by design: if the service is down, 404s, or the
 *  section request fails, the theme's normal "no results" state simply stays.
 *
 *  Note the service must send `Access-Control-Allow-Origin` for the storefront
 *  origin, otherwise the browser blocks the response and the fallback never
 *  renders (a single console warning is logged when a lookup fails).
 */
if (!customElements.get("search-fallback")) {
  class SearchFallback extends HTMLElement {
    // Markers the section uses to recognise a fallback request and pick the
    // markup variant, see sections/search-fallback-results.liquid.
    static get MARKERS() {
      return { drawer: "sfb-rows--", grid: "sfb-grid--" };
    }

    // Plenty for one session of typing, and keeps the map from growing forever.
    static get CACHE_LIMIT() {
      return 50;
    }

    // Enough placeholders to read as "a list is coming", few enough to not push
    // the real content out of view when it arrives.
    static get SKELETON_COUNT() {
      return { drawer: 3, grid: 4 };
    }

    connectedCallback() {
      this.endpoint = (this.dataset.endpoint || "").trim().replace(/\/+$/, "");
      this.mode = this.dataset.mode === "grid" ? "grid" : "drawer";
      this.limit = parseInt(this.dataset.limit, 10) || 6;

      const debounce = parseInt(this.dataset.debounce, 10);
      this.debounceDelay = isNaN(debounce) ? 300 : debounce;
      // A two-letter fragment gives a semantic model nothing to work with, so
      // short terms are dropped before they cost a lookup.
      const minLength = parseInt(this.dataset.minLength, 10);
      this.minLength = isNaN(minLength) ? 4 : minLength;

      // term -> markup HTML, or "" when neither the service nor the store had
      // anything. Cached misses matter most: they keep a dead term from being
      // looked up again every time the shopper edits around it.
      this.cache = new Map();
      this.requestToken = 0;
      this.hasWarned = false;
      this.loadingEl = null;
      this.loadingHost = null;

      if (!this.endpoint) return;

      if (this.mode === "grid") {
        const term = (this.dataset.query || "").trim();
        // One shot on page load, so there is nothing to debounce.
        if (term.length >= this.minLength) this.search(term);
        return;
      }

      this.onNoResults = (event) => {
        const term = event.detail && event.detail.query;
        if (term) this.queueSearch(term.trim());
      };
      document.addEventListener("search:no-results", this.onNoResults);
    }

    disconnectedCallback() {
      clearTimeout(this.debounceTimer);
      this.hideLoading();
      if (this.onNoResults) {
        document.removeEventListener("search:no-results", this.onNoResults);
      }
    }

    // Debounced, cache-first entry point.
    queueSearch(term) {
      clearTimeout(this.debounceTimer);
      if (!term || term.length < this.minLength) {
        this.hideLoading();
        return;
      }

      if (this.cache.has(term)) {
        // Invalidates anything still in flight, then renders straight away.
        this.requestToken++;
        this.hideLoading();
        const markup = this.cache.get(term);
        if (markup) this.render(markup, term);
        return;
      }

      // Shown before the debounce, not after: the empty state is already on
      // screen at this point, so waiting would read as a settled answer.
      this.showLoading();
      this.debounceTimer = setTimeout(
        () => this.search(term),
        this.debounceDelay,
      );
    }

    search(term) {
      // Typing keeps firing searches; only the newest one may render.
      const token = ++this.requestToken;
      this.classList.add("loading");
      this.showLoading();

      this.fetchHandles(term)
        .then((handles) => {
          if (token !== this.requestToken) return null;
          return handles.length ? this.fetchProductsMarkup(handles) : "";
        })
        .then((markup) => {
          if (token !== this.requestToken || markup === null) return;
          this.remember(term, markup);
          if (markup) this.render(markup, term);
        })
        .catch((error) => {
          // Keep the theme's own empty state, but leave a trace: a blocked
          // CORS response or a sleeping service looks just like a miss.
          if (!this.hasWarned) {
            this.hasWarned = true;
            console.warn("[search-fallback] lookup failed:", error);
          }
        })
        .finally(() => {
          // A newer search owns the placeholder by now, so only the current one
          // may take it down.
          if (token !== this.requestToken) return;
          this.classList.remove("loading");
          this.hideLoading();
        });
    }

    // Placeholder rows/cards for the wait. Kept out of the way of everything
    // else: it is a single node this element owns and removes again, so a miss
    // leaves the theme's own markup exactly as it found it.
    showLoading() {
      if (this.loadingEl && this.loadingEl.isConnected) return;

      const target = this.getTarget();
      if (!target) return;

      const node = document.createElement("div");
      node.className = `search-fallback-loading search-fallback-loading--${this.mode}`;
      node.setAttribute("data-search-fallback-loading", "");
      // Nothing here carries meaning, and the results that follow announce
      // themselves — so it stays out of the accessibility tree.
      node.setAttribute("aria-hidden", "true");
      node.innerHTML = this.skeletonMarkup();

      if (this.mode === "grid") {
        target.appendChild(node);
      } else {
        // Where the real rows land too. The marker lets CSS take down the
        // "no results" line and the trending products for the moment: the
        // answer is not in yet, so neither should be on screen.
        const emptyState = target.querySelector(
          ".quick-search-no-products-container",
        );
        if (emptyState) emptyState.insertAdjacentElement("afterend", node);
        else target.prepend(node);
        target.setAttribute("data-search-fallback-loading", "");
      }

      // The grid's empty state lives outside this element, so CSS cannot reach
      // it from the marker — it is hidden by hand and put back on a miss.
      this.hideEmptyState();

      this.loadingEl = node;
      this.loadingHost = target;
    }

    hideLoading() {
      if (this.loadingEl) this.loadingEl.remove();
      if (this.loadingHost) {
        this.loadingHost.removeAttribute("data-search-fallback-loading");
      }
      this.loadingEl = null;
      this.loadingHost = null;
      // Only ever restores what the placeholder hid: once results render, they
      // take the empty state off this list and it stays down for good.
      this.restoreEmptyState();
    }

    hideEmptyState() {
      const selector = this.dataset.hide;
      if (!selector) return;

      this.hiddenEls = this.hiddenEls || [];
      document.querySelectorAll(selector).forEach((el) => {
        if (el.hasAttribute("hidden")) return;
        el.setAttribute("hidden", "");
        el.style.display = "none";
        this.hiddenEls.push(el);
      });
    }

    restoreEmptyState() {
      if (!this.hiddenEls) return;
      this.hiddenEls.forEach((el) => {
        el.removeAttribute("hidden");
        el.style.display = "";
      });
      this.hiddenEls = [];
    }

    skeletonMarkup() {
      const count =
        SearchFallback.SKELETON_COUNT[this.mode] ||
        SearchFallback.SKELETON_COUNT.drawer;
      const item =
        this.mode === "grid"
          ? '<div class="search-fallback-loading__card">' +
            '<span class="search-fallback-loading__media"></span>' +
            '<span class="search-fallback-loading__line"></span>' +
            '<span class="search-fallback-loading__line search-fallback-loading__line--short"></span>' +
            "</div>"
          : '<div class="search-fallback-loading__row">' +
            '<span class="search-fallback-loading__thumb"></span>' +
            '<span class="search-fallback-loading__meta">' +
            '<span class="search-fallback-loading__line"></span>' +
            '<span class="search-fallback-loading__line search-fallback-loading__line--short"></span>' +
            "</span></div>";

      return item.repeat(Math.min(count, this.limit || count));
    }

    remember(term, markup) {
      if (this.cache.size >= SearchFallback.CACHE_LIMIT) {
        this.cache.delete(this.cache.keys().next().value);
      }
      this.cache.set(term, markup);
    }

    // Asks the fallback service for matching products. A 404 means it found
    // nothing either, which is an expected answer rather than an error.
    fetchHandles(term) {
      const url = `${this.endpoint}/search?query=${encodeURIComponent(term)}&shop=${encodeURIComponent(window.Shopify.shop)}`;

      return fetch(url)
        .then((response) => {
          if (response.status === 404) return null;
          if (!response.ok) throw new Error(response.status);
          return response.json();
        })
        .then((data) => {
          if (!data || !Array.isArray(data.handles)) return [];
          return data.handles
            .filter((handle) => typeof handle === "string" && handle.length)
            .slice(0, this.limit);
        });
    }

    // Renders the handles through the theme so the fallback list looks exactly
    // like a normal result list. Resolves to the markup, or "" when none of the
    // handles exist in this store.
    fetchProductsMarkup(handles) {
      const root =
        (window.Shopify &&
          window.Shopify.routes &&
          window.Shopify.routes.root) ||
        "/";
      const query = SearchFallback.MARKERS[this.mode] + handles.join(",");
      const url = `${root}search?q=${encodeURIComponent(
        query,
      )}&type=product&section_id=search-fallback-results`;

      return fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const selector =
            this.mode === "grid"
              ? "[data-search-fallback-grid]"
              : "[data-search-fallback-rows]";
          const fresh = new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector(selector);
          // No children means every handle was unknown to this store.
          return fresh && fresh.children.length ? fresh.outerHTML : "";
        });
    }

    render(markup, term) {
      const target = this.getTarget();
      if (!target) return;

      target.innerHTML = markup;
      target.setAttribute("data-search-fallback-for", term);

      this.hideEmptyState();
      // There are results now, so the empty state is not coming back — dropping
      // the list keeps the placeholder from restoring it on its way out.
      this.hiddenEls = [];
    }

    // Resolved late: the drawer replaces its results markup on every keystroke.
    getTarget() {
      if (this.mode === "grid") return this;

      const scope = this.closest("predictive-search-v2") || document;
      const target = scope.querySelector(
        this.dataset.target || ".quick-search-main-container",
      );
      if (!target) return null;

      // The drawer may have rendered real results while the fallback was in
      // flight — the empty state marker is gone in that case, so leave it be.
      return target.querySelector(".quick-search-no-products-container")
        ? target
        : null;
    }
  }

  customElements.define("search-fallback", SearchFallback);
}
